import { describeApiError, type ActivitySettings, type DirectoryUser, type Room } from '@brush/api-client';
import { ErrorNotice, useDebouncedValue } from '@brush/ui';
import { Search, ToggleLeft, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  operatorOverride,
  overridableTypes,
  overrideTargetText,
  playerOverride,
  type OverrideRequest,
} from '../activity/settings';
import { useDirectorySearch, usePutActivityOverride, useRemoveActivityOverride } from '../queries';

const inputClass =
  'w-full rounded-lg border border-ink-800/15 px-3 py-2 text-sm text-ink-950 outline-none focus:border-felt-500';

type Scope = OverrideRequest['scope'];

const SCOPES: { scope: Scope; label: string }[] = [
  { scope: 'platform', label: 'Everyone' },
  { scope: 'room', label: 'A room' },
  { scope: 'operator', label: 'A staff member' },
  { scope: 'player', label: 'A player' },
];

/** The directory's minimum, as the operators section mirrors it: a shorter search is not sent at all. */
const MIN_QUERY = 2;

/**
 * Which usage is recorded, and for whom (ADR 0012 Decision 12). Only engagement types are offered — the list
 * is the API's, which never carries an audit type. A staff member is chosen from the directory and a player
 * by card at a room; no id is typed.
 */
export function ActivityOverridesPanel({ settings, rooms }: { settings: ActivitySettings; rooms: readonly Room[] }) {
  const types = useMemo(() => overridableTypes(settings.types), [settings.types]);
  const label = useMemo(() => new Map(types.map((t) => [t.code, t.label])), [types]);
  const roomName = useMemo(() => {
    const names = new Map(rooms.map((r) => [r.id, r.name]));
    return (id: string) => names.get(id) ?? null;
  }, [rooms]);

  const [code, setCode] = useState('');
  const [scope, setScope] = useState<Scope>('platform');
  const [roomId, setRoomId] = useState('');
  const [cardId, setCardId] = useState('');
  const [person, setPerson] = useState<DirectoryUser | null>(null);
  const [query, setQuery] = useState('');
  const [enabled, setEnabled] = useState(false);

  const debounced = useDebouncedValue(query.trim(), 400);
  const search = useDirectorySearch(debounced, scope === 'operator' && debounced.length >= MIN_QUERY && person === null);
  const put = usePutActivityOverride();
  const remove = useRemoveActivityOverride();

  const request: OverrideRequest | null = !code
    ? null
    : scope === 'platform'
      ? { code, scope, roomId: null, operatorObjectId: null, cardId: null, enabled }
      : scope === 'room'
        ? roomId ? { code, scope, roomId, operatorObjectId: null, cardId: null, enabled } : null
        : scope === 'operator'
          ? person ? operatorOverride(code, person, enabled) : null
          : roomId && cardId.trim() ? playerOverride(code, roomId, cardId, enabled) : null;

  const add = () =>
    request && put.mutate(request, { onSuccess: () => { setPerson(null); setQuery(''); setCardId(''); } });

  return (
    <section className="overflow-hidden rounded-xl border border-ink-800/10 bg-white shadow-sm">
      <header className="border-b border-ink-800/10 px-6 py-4">
        <h2 className="flex items-center gap-2 font-display font-semibold text-ink-950">
          <ToggleLeft className="size-4 text-ink-800/60" /> Usage tracking
        </h2>
        <p className="mt-1 text-xs text-ink-800/60">
          Switch a kind of usage off — or back on — for everyone, a room, a staff member or a player. The
          nearest says: a person&apos;s own setting, then their room&apos;s, then the platform&apos;s. Changes
          reach every server within a minute. Audit — who changed what — is always recorded and cannot be
          switched off.
        </p>
      </header>

      <div className="space-y-5 p-6">
        <ErrorNotice message={describeApiError(remove.error)} />
        {settings.overrides.length === 0 ? (
          <p className="text-xs text-ink-800/60">Nothing is switched off: every kind of usage is recorded as the catalogue sets it.</p>
        ) : (
          <ul className="divide-y divide-ink-800/10 rounded-lg border border-ink-800/10">
            {settings.overrides.map((o) => (
              <li key={o.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                <span>
                  <strong className="text-ink-950">{label.get(o.code) ?? o.code}</strong>
                  <span className="text-ink-800/70"> — {overrideTargetText(o, roomName)}: </span>
                  <span className={o.enabled ? 'text-felt-700' : 'text-red-700'}>{o.enabled ? 'on' : 'off'}</span>
                </span>
                <button type="button" onClick={() => remove.mutate(o.id)} disabled={remove.isPending}
                  className="inline-flex items-center gap-1 text-xs text-ink-800/70 hover:text-red-700">
                  <Trash2 className="size-3.5" /> Remove
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="space-y-3 rounded-lg border border-dashed border-ink-800/15 p-3">
          <label className="block text-sm">
            <span className="mb-1 block text-xs text-ink-800/60">Kind of usage</span>
            <select value={code} onChange={(e) => setCode(e.target.value)} className={inputClass}>
              <option value="">Choose…</option>
              {types.map((t) => (
                <option key={t.code} value={t.code}>{t.label}</option>
              ))}
            </select>
          </label>

          <fieldset className="flex flex-wrap gap-3 text-sm">
            <legend className="mb-1 text-xs text-ink-800/60">For</legend>
            {SCOPES.map((s) => (
              <label key={s.scope} className="inline-flex items-center gap-1.5">
                <input type="radio" name="override-scope" checked={scope === s.scope}
                  onChange={() => { setScope(s.scope); put.reset(); }} />
                {s.label}
              </label>
            ))}
          </fieldset>

          {scope === 'room' || scope === 'player' ? (
            <label className="block text-sm">
              <span className="mb-1 block text-xs text-ink-800/60">{scope === 'player' ? 'The room that issued the card' : 'Room'}</span>
              <select value={roomId} onChange={(e) => setRoomId(e.target.value)} className={inputClass}>
                <option value="">Choose a room…</option>
                {rooms.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </label>
          ) : null}

          {scope === 'player' ? (
            <label className="block text-sm">
              <span className="mb-1 block text-xs text-ink-800/60">Card number at that room</span>
              <input value={cardId} onChange={(e) => setCardId(e.target.value)} className={inputClass} placeholder="00142" />
            </label>
          ) : null}

          {scope === 'operator' ? (
            person ? (
              <p className="text-sm text-ink-950">
                {person.displayName} <span className="text-xs text-ink-800/60">{person.mail ?? person.userPrincipalName}</span>{' '}
                <button type="button" className="text-xs text-felt-700 underline" onClick={() => setPerson(null)}>Change</button>
              </p>
            ) : (
              <div className="text-sm">
                <span className="mb-1 block text-xs text-ink-800/60">Find somebody in the staff directory</span>
                <div className="relative">
                  <Search className="pointer-events-none absolute left-2.5 top-2.5 size-4 text-ink-800/40" />
                  <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Name or work address"
                    className={`${inputClass} pl-8`} />
                </div>
                <ErrorNotice message={describeApiError(search.error)} className="mt-2" />
                <ul className="mt-2 space-y-1">
                  {(search.data?.data ?? []).map((u) => (
                    <li key={u.objectId}>
                      <button type="button" onClick={() => setPerson(u)}
                        className="w-full rounded-lg px-2 py-1 text-left hover:bg-felt-50/50">
                        {u.displayName} <span className="text-xs text-ink-800/60">{u.mail ?? u.userPrincipalName}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )
          ) : null}

          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            Recorded (untick to switch it off)
          </label>

          <ErrorNotice message={describeApiError(put.error)} />
          <button type="button" onClick={add} disabled={!request || put.isPending}
            className="rounded-lg bg-felt-700 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50">
            Save
          </button>
        </div>
      </div>
    </section>
  );
}
